import type { ScopeRow } from '../scope';
import { buildScopeRows } from '../scope';

import type { ScopeConfiguration } from './types';

// Catalog key of the single managed QA expert (mirrors the backend ``QA_EXPERT_KEY``); one stable
// key serves every (sub-org, LOB) permutation, with per-scope prompt / structured-output overrides.
export const QA_EXPERT_KEY = 'qa_expert';

// Re-exported rather than defined here: every Clive Configuration tab derives the same rows,
// so they live in ``CliveConfiguration/scope.ts``. Kept as a named export from this module so
// the tab's existing imports and its scope.test.ts are unaffected.
export { buildScopeRows };

/**
 * The stored row for one scope, or `undefined` when the scope has never been configured.
 *
 * A flat organization's rows carry a `null` sub_organization_id, so an org-level row and a
 * sub-organization row that happen to share a LOB are different scopes and must not match
 * each other.
 */
export const findScopeConfiguration = (
  configurations: ScopeConfiguration[],
  scope: ScopeRow
): ScopeConfiguration | undefined =>
  configurations.find(
    (configuration) =>
      configuration.sub_organization_id === scope.sub_organization_id && configuration.lob === scope.lob
  );

// Per-scope display state for the scope table: whether the QA expert runs for this
// (sub-org, LOB), whether a prompt is stored, and how many structured-output fields it defines.
export interface ScopeStatus {
  enabled: boolean;
  hasInstructions: boolean;
  configuredFieldsCount: number;
}

/**
 * How one scope reads in the table.
 *
 * "Enabled" is the disjunction of the two independent run modes — the expert runs for the scope
 * under the conductor, standalone, or both — so it is on exactly when the row is not off. A
 * scope with no row at all reads identically to a row with both modes off, which is what makes
 * the backend's "absent rather than listed as off" answer safe to render.
 */
export const scopeStatus = (configuration?: ScopeConfiguration): ScopeStatus => ({
  enabled: Boolean(configuration?.conductor_enabled || configuration?.standalone_enabled),
  hasInstructions: Boolean(configuration?.instructions),
  configuredFieldsCount: configuration?.configured_fields?.length ?? 0,
});
